/**
 * #1034 — the Polymarket US slot in a balance line (the Positions page rail
 * band and the board pop-up), beside K and P. Like them it shows the balance
 * the venue reports — here Polymarket US's current cash, as last saved by the
 * relay — with no hold math, so it is never an available-after-orders figure
 * or a member's share of a shared account. The relay saves it when the
 * account is first set up and before each order placed on it, not on a timer,
 * so its hover says when it was saved.
 *
 * One figure per account the trader can see the cash of, never added
 * together. A shared account where the trader views only their own orders
 * shows "—": that cash is the desk's. A failed read is marked ⚠ inline; a
 * failed refresh keeps the figure already shown (as K and P keep theirs) with
 * the reason in its hover. Each hover names its account. A new login never
 * inherits a figure.
 *
 * Shown in whole cents beside K and P, cut toward zero (never rounded), with
 * the exact saved text in its hover when that has more digits.
 *
 * Only for logins with US. While that is not known yet the slot keeps its
 * place unseen, so the header does not grow when the answer lands; a failed
 * check is not "no US", so it shows "—⚠" and says it could not check.
 *
 * Its own module, not PositionSidebar's: tests that stub PositionSidebar while
 * rendering the Positions page keep working without knowing about it.
 */
import type { UsRail } from './useUsRail';
import type { UsSavedBalance } from '../../lib/polymarketUs/portfolioClient';
import type { UsRailBalance } from './usRailModel';
import { formatRecordedUtc } from '../PolymarketUs/UsOrderTable';

const AS_REPORTED = 'Polymarket US balance as reported by Polymarket US (current cash, not including holdings). '
  + 'G1 applies no hold math; do not read this as a member allocation or an available-after-order-holds figure.';
export const US_BALANCE_LOADING_TITLE = 'Reading the Polymarket US balance…';
export const US_BALANCE_SHARED_TITLE = 'This shared Polymarket US account’s balance belongs to the desk; you view only your own orders on it.';
export const US_BALANCE_NONE_TITLE = 'No Polymarket US balance has been saved for this account yet.';
export const usBalanceUnavailableTitle = (code: string) => `Couldn’t read the Polymarket US balance (${code}).`;
export const usBalanceUncheckedTitle = (code: string) =>
  `Couldn’t check whether this login has a Polymarket US account (${code}). Retrying every 10 s.`;

/** Exact decimal text as dollars: thousands commas and at least two decimals,
 * from the text itself — nothing is rounded ("1234.5" → "$1,234.50"). */
export function usBalanceDollars(text: string): string {
  const m = /^(-?)(\d+)(?:\.(\d+))?$/.exec(text);
  if (!m) return `$${text}`;
  const whole = m[2].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${m[1]}$${whole}.${(m[3] ?? '').padEnd(2, '0')}`;
}

/** The figure as shown beside K and P: whole cents, cut toward zero from the
 * text itself (never rounded), with thousands commas ("1234.56875" →
 * "$1,234.56"). A figure that cuts to zero carries no sign. */
function usBalanceCents(text: string): string {
  const m = /^(-?)(\d+)(?:\.(\d+))?$/.exec(text);
  if (!m) return `$${text}`;
  const whole = m[2].replace(/^0+(?=\d)/, '');
  const cents = (m[3] ?? '').padEnd(2, '0').slice(0, 2);
  const sign = m[1] && !(whole === '0' && cents === '00') ? '-' : '';
  return `${sign}$${whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}.${cents}`;
}

/** Digits past the cent that are not zero: the shown figure is not the whole value. */
const hasSubCent = (text: string) => /[1-9]/.test(/^-?\d+\.\d{2}(\d+)$/.exec(text)?.[1] ?? '');

export const usBalanceRefreshFailedTitle = (code: string) =>
  `The latest refresh failed (${code}); this is the last reading shown.`;
const saved = (b: UsSavedBalance) =>
  `${AS_REPORTED} Saved ${formatRecordedUtc(b.observedAtMs)} UTC. G1 saves it when the account is first set up and before each order placed on it, not on a timer.${
    hasSubCent(b.currentBalance) ? ` Exact balance ${usBalanceDollars(b.currentBalance)}; shown cut to the cent.` : ''}`;

type Piece = { text: string; title: string; failed: boolean };

function piece(balance: UsRailBalance): Piece {
  switch (balance.state) {
    case 'saved':
      return { text: usBalanceCents(balance.balance.currentBalance), title: saved(balance.balance), failed: false };
    case 'pending': return { text: '—', title: US_BALANCE_LOADING_TITLE, failed: false };
    case 'none': return { text: '—', title: US_BALANCE_NONE_TITLE, failed: false };
    case 'shared': return { text: '—', title: US_BALANCE_SHARED_TITLE, failed: false };
    case 'unavailable':
      // A refresh failed after a reading was shown: keep it (as K and P keep
      // theirs) and say so, with the time that reading was saved.
      return balance.last
        ? { text: usBalanceCents(balance.last.currentBalance),
          title: `${saved(balance.last)} ${usBalanceRefreshFailedTitle(balance.error)}`, failed: true }
        : { text: '—', title: usBalanceUnavailableTitle(balance.error), failed: true };
  }
}

/** Which account a figure is (a trader may see several): "Account …1a2b (personal)". */
export const usBalanceAccountLabel = (accountId: string, ownership: 'personal' | 'pooled') =>
  `Account …${accountId.slice(-4)} (${ownership === 'personal' ? 'personal' : 'shared desk'}).`;

export function UsBalanceSlot({ rail, className = '', valueClassName = '', focusable = true, descriptionId }: {
  rail: Pick<UsRail, 'hasUs' | 'read' | 'error' | 'status'>; className?: string; valueClassName?: string;
  /** A host button already owns keyboard focus; avoid a nested tab stop. */
  focusable?: boolean;
  /** Connect noninteractive account details to a containing control. */
  descriptionId?: string;
}) {
  if (rail.hasUs === false) return null;
  if (rail.hasUs === null && rail.status !== 'unavailable') {
    // Not known yet whether this login has US (most logins have none): hold
    // the slot's place unseen, claiming nothing, so the header does not grow
    // and push the panel when the answer lands.
    return (
      <span aria-hidden="true" className={`invisible max-w-full min-w-0 ${className}`} data-testid="us-balance-slot-reserved">
        <span className="text-[10px]">US saved</span>{' '}<span className={valueClassName}>—</span>
      </span>
    );
  }
  // The last good read stays through a failed one (as K and P keep theirs),
  // and each figure then says the refresh failed.
  const stale = rail.status === 'unavailable' && rail.error ? rail.error : null;
  const pieces = rail.hasUs === null
    // The check failed: not "no US". Marked as a failure, with what it is doing.
    ? [{ key: 'unchecked', text: '—', failed: true, title: usBalanceUncheckedTitle(rail.error ?? 'US_READ_UNAVAILABLE') }]
    : rail.read
    ? rail.read.accounts.map((a) => {
      const p = piece(a.balance);
      const failed = p.failed || (stale !== null && a.balance.state === 'saved');
      const title = `${usBalanceAccountLabel(a.accountId, a.ownership)} ${p.title}${
        !p.failed && failed ? ` ${usBalanceRefreshFailedTitle(stale!)}` : ''}`;
      return { key: a.accountId, text: p.text, title, failed };
    })
    : [{ key: 'pending', text: '—', failed: rail.error !== null,
      title: rail.error ? usBalanceUnavailableTitle(rail.error) : rail.status === 'off'
        ? 'Polymarket US balance has not been read in this view. Open the positions panel to check.'
        : US_BALANCE_LOADING_TITLE }];
  return (
    // Never clipped: a cut-off figure reads as a smaller, real-looking amount.
    // The slot may shrink to its line, and a figure wider than the rail breaks
    // onto the next line with every digit kept.
    <span className={`max-w-full min-w-0 ${className}`} data-testid="us-balance-slot">
      <span className="text-[10px]">US saved</span>{' '}
      {pieces.map((p, i) => (
        <span key={p.key}>
          {i > 0 && ' · '}
          {/* Focusable, so keyboard and screen-reader users reach what the
              hover says; a failure is marked inline, not only on hover. */}
          <span className={`[overflow-wrap:anywhere] ${valueClassName}`} title={p.title}
            aria-label={p.key === 'unchecked' ? `Polymarket US balance unknown. ${p.title}` : `Polymarket US ${p.text}. ${p.title}`}
            tabIndex={focusable ? 0 : undefined} data-testid="us-balance-value" data-failed={p.failed || undefined}>
            {p.text}
            {p.failed && <span aria-hidden="true" className="ml-0.5 text-amber-400 light:text-amber-700">⚠</span>}
          </span>
        </span>
      ))}
      {descriptionId && <span id={descriptionId} className="sr-only">Polymarket US saved balance: {
        pieces.map(p => `${p.text === '—' ? 'unknown' : p.text}. ${p.title}`).join(' ')}</span>}
    </span>
  );
}
